import { Component } from '@theme/component';
import { debounce, prefersReducedMotion, hasFinePointer } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * @type {AbortController | null}
   */
  #productCarouselController = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#syncHeaderMenuStyles();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#focusSearchInputOnOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.#setupProductCarousels();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#productCarouselController?.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    this.#syncHeaderMenuStyles();

    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  /**
   * Auto-focus the search input when the drawer opens, but only on fine-pointer
   * (mouse-driven) devices. dialog.js intentionally suppresses initial focus (placing it on
   * the dialog itself) to avoid the mobile soft keyboard popping up. This event fires after
   * that suppression, so focusing here overrides it for desktop while leaving touch untouched.
   */
  #focusSearchInputOnOpen = () => {
    if (hasFinePointer()) {
      this.refs.searchInput.focus({ preventScroll: true });
    }
  };

  #syncHeaderMenuStyles() {
    const headerMenuList = document.querySelector('header-menu .menu-list');
    const headerSubmenu = document.querySelector('header-menu .menu-list__submenu-inner');

    if (headerMenuList instanceof HTMLElement) {
      const headerMenuStyles = getComputedStyle(headerMenuList);

      this.style.setProperty('--drawer-menu-parent-font-family', headerMenuStyles.getPropertyValue('--menu-top-level-font-family'));
      this.style.setProperty('--drawer-menu-parent-font-style', headerMenuStyles.getPropertyValue('--menu-top-level-font-style'));
      this.style.setProperty('--drawer-menu-parent-font-weight', headerMenuStyles.getPropertyValue('--menu-top-level-font-weight'));
      this.style.setProperty('--drawer-menu-parent-font-size', headerMenuStyles.getPropertyValue('--menu-top-level-font-size-desktop'));
      this.style.setProperty('--drawer-menu-parent-font-line-height', headerMenuStyles.getPropertyValue('--menu-top-level-font-line-height'));
      this.style.setProperty('--drawer-menu-parent-font-case', headerMenuStyles.getPropertyValue('--menu-top-level-font-case'));
      this.style.setProperty('--drawer-menu-parent-font-color', 'var(--drawer-search-results-color)');
    }

    if (headerSubmenu instanceof HTMLElement) {
      const headerSubmenuStyles = getComputedStyle(headerSubmenu);

      this.style.setProperty('--drawer-menu-child-font-family', headerSubmenuStyles.getPropertyValue('--menu-child-font-family'));
      this.style.setProperty('--drawer-menu-child-font-style', headerSubmenuStyles.getPropertyValue('--menu-child-font-style'));
      this.style.setProperty('--drawer-menu-child-font-weight', headerSubmenuStyles.getPropertyValue('--menu-child-font-weight'));
      this.style.setProperty('--drawer-menu-child-font-size', headerSubmenuStyles.getPropertyValue('--menu-child-font-size'));
      this.style.setProperty('--drawer-menu-child-font-line-height', headerSubmenuStyles.getPropertyValue('--menu-child-font-line-height'));
      this.style.setProperty('--drawer-menu-child-font-case', headerSubmenuStyles.getPropertyValue('--menu-child-font-case'));
      this.style.setProperty('--drawer-menu-child-font-color', 'var(--drawer-search-results-color)');
    }
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__suggestions, ' +
          '.predictive-search-results__collections-list, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  #setupProductCarousels() {
    this.#productCarouselController?.abort();
    this.#productCarouselController = new AbortController();
    const { signal } = this.#productCarouselController;

    const scrollers = Array.from(this.querySelectorAll('.predictive-search-results__wrapper-products--drawer')).filter(
      (scroller) => scroller instanceof HTMLElement
    );

    for (const scroller of scrollers) {
      let frame = 0;
      const schedulePreload = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          this.#preloadProductCarouselImages(scroller);
        });
      };

      scroller.addEventListener('scroll', schedulePreload, { signal, passive: true });
      this.#preloadProductCarouselImages(scroller);
    }
  }

  /**
   * @param {HTMLElement} scroller
   */
  #preloadProductCarouselImages(scroller) {
    const cards = Array.from(scroller.querySelectorAll('.predictive-search-results__card')).filter(
      (card) => card instanceof HTMLElement
    );
    const preloadStart = scroller.scrollLeft - scroller.clientWidth * 0.25;
    const preloadEnd = scroller.scrollLeft + scroller.clientWidth * 2;

    for (const card of cards) {
      const cardStart = card.offsetLeft;
      const cardEnd = cardStart + card.offsetWidth;

      if (cardEnd < preloadStart || cardStart > preloadEnd) continue;

      const images = Array.from(card.querySelectorAll('img')).filter((image) => image instanceof HTMLImageElement);

      for (const image of images) {
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = image.fetchPriority || 'low';

        if (!image.complete && image.currentSrc) {
          image.decode?.().catch(() => {});
        }
      }
    }
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#setupProductCarousels();
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#setupProductCarousels();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
